// Typed wrappers for the /v1/merchants/:id/users/* surface.
//
// AUDIT-PASS-6 follow-up. Replaces the "type the numeric auth-service
// user_id" UX in AddMemberModal with email-based lookup + invite. The
// backend forwards the operator's bearer to auth-service for the
// actual user resolution / grant upsert.
//
// Two endpoints:
//
//   resolveUserByEmail — GET .../users/lookup?email=<email>
//     Returns {user_id, email} when auth-service already has a user
//     with that email. Throws UserNotFoundError (mapped from 404 body
//     code "user_not_found") when the email is not yet known — that's
//     the panel's signal to surface the Invite branch.
//
//   inviteUser — POST .../users/invite {email, role}
//     Pre-provisions the user in auth-service AND creates the local
//     membership row in a single backend call. Returns the resolved
//     user_id and the membership row. Idempotent: an already-existing
//     membership returns the same shape with already_member=true rather
//     than a 409.
//
// BFF rule (CLAUDE.md rule 1): all calls go through apiClient — the
// single axios instance that injects Authorization: Bearer + X-Merchant-ID.
import axios from "axios";

import { apiClient } from "./client";
import type { Membership, MembershipRole } from "./memberships";

/** Thrown when /users/lookup returns 404 user_not_found. Distinct from
 *  a generic axios error so the modal can branch on "not yet a user" vs
 *  "auth-service unreachable / permission denied / etc." */
export class UserNotFoundError extends Error {
  constructor(public email: string) {
    super(`No payouts user with email ${email}`);
    this.name = "UserNotFoundError";
  }
}

export interface ResolvedUser {
  user_id: number;
  email: string;
}

/**
 * resolveUserByEmail — GET /v1/merchants/:merchantId/users/lookup
 *
 * Resolves an email to its auth-service user_id via payouts-backend.
 * Returns the {user_id, email} on hit. Throws UserNotFoundError when
 * the backend returns 404 user_not_found (no auth-service user has
 * that email yet — the caller should surface the Invite flow). Other
 * axios errors propagate unchanged so the caller can route them
 * through apiErrorMessage.
 */
export async function resolveUserByEmail(
  merchantId: number,
  email: string,
): Promise<ResolvedUser> {
  try {
    const resp = await apiClient.get<ResolvedUser>(
      `/v1/merchants/${merchantId}/users/lookup`,
      { params: { email } },
    );
    return resp.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      const code = (err.response.data as { code?: string } | undefined)?.code;
      if (code === "user_not_found") {
        throw new UserNotFoundError(email);
      }
    }
    throw err;
  }
}

export interface InviteResult {
  user_id: number;
  email: string;
  membership: Membership;
  /** When true, the user was already on this merchant team — the call
   *  was idempotent. The panel surfaces a softer success copy in this
   *  case ("Already on the team") to avoid the misleading "Added" toast
   *  for a no-op invite. */
  already_member: boolean;
}

/**
 * inviteUser — POST /v1/merchants/:merchantId/users/invite
 *
 * Pre-provisions the user in auth-service (creating their record if
 * absent — first-OTP onboarding is deferred until they log in) AND
 * creates the local merchant_memberships row in one backend call.
 *
 * Idempotent: a second invite for an already-attached user returns
 * already_member=true and the existing membership row, rather than
 * the 409 membership_exists the bare /memberships POST would return.
 */
export async function inviteUser(
  merchantId: number,
  payload: {
    email: string;
    role: MembershipRole;
    /**
     * Human-friendly name captured at invite time. Stored on the
     * merchant_memberships row (migration 024); returned on
     * listMemberships so the Rbac table can render it. Optional —
     * the backend tolerates an empty / missing field and the panel
     * falls back to email then "User #N".
     */
    display_name?: string;
  },
): Promise<InviteResult> {
  const resp = await apiClient.post<InviteResult>(
    `/v1/merchants/${merchantId}/users/invite`,
    payload,
  );
  return resp.data;
}
